import { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext } from '../App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { Users, FileText, TrendingUp, Award, LogOut, LayoutDashboard } from 'lucide-react';

export default function AdminDashboard() {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const { user, logout } = useContext(AuthContext);
  const navigate = useNavigate();

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      const response = await axios.get(`${API}/admin/analytics`);
      setAnalytics(response.data);
    } catch (error) {
      toast.error('Failed to fetch analytics');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
    toast.success('Logged out successfully');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-sky-50 via-white to-blue-50">
      {/* Header */}
      <header className="bg-white border-b border-sky-100 sticky top-0 z-40 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <LayoutDashboard className="h-7 w-7 text-sky-600" />
            <div>
              <h1 className="text-xl font-bold text-gray-900" style={{fontFamily: 'Space Grotesk, sans-serif'}}>ESAP Admin</h1>
              <p className="text-sm text-gray-500">Dashboard</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right hidden sm:block">
              <p className="text-sm font-medium text-gray-900">{user?.name}</p>
              <p className="text-xs text-gray-500">{user?.email}</p>
            </div>
            <Button variant="outline" onClick={handleLogout} data-testid="admin-logout-btn" className="border-sky-200">
              <LogOut className="h-4 w-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      {/* Navigation */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex gap-6 py-3">
            <Button variant="ghost" className="text-sky-600" onClick={() => navigate('/admin/dashboard')} data-testid="nav-dashboard">
              Dashboard
            </Button>
            <Button variant="ghost" onClick={() => navigate('/admin/students')} data-testid="nav-students">
              Students
            </Button>
            <Button variant="ghost" onClick={() => navigate('/admin/exams')} data-testid="nav-exams">
              Exams
            </Button>
            <Button variant="ghost" onClick={() => navigate('/admin/categories')} data-testid="nav-categories">
              Categories
            </Button>
            <Button variant="ghost" onClick={() => navigate('/admin/question-bank')} data-testid="nav-question-bank">
              Question Bank
            </Button>
            <Button variant="ghost" onClick={() => navigate('/admin/results')} data-testid="nav-results">
              Results
            </Button>
          </nav>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {loading ? (
          <div className="text-center py-12">
            <div className="spinner mx-auto mb-4"></div>
            <p className="text-gray-500">Loading analytics...</p>
          </div>
        ) : (
          <>
            {/* Stats Cards */}
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <StatCard
                icon={<Users className="h-8 w-8 text-sky-600" />}
                title="Total Students"
                value={analytics.total_students}
                bgColor="bg-sky-50"
              />
              <StatCard
                icon={<FileText className="h-8 w-8 text-blue-600" />}
                title="Total Exams"
                value={analytics.total_exams}
                bgColor="bg-blue-50"
              />
              <StatCard
                icon={<TrendingUp className="h-8 w-8 text-green-600" />}
                title="Exam Attempts"
                value={analytics.total_attempts}
                bgColor="bg-green-50"
              />
              <StatCard
                icon={<Award className="h-8 w-8 text-purple-600" />}
                title="Pass Rate"
                value={`${analytics.pass_rate}%`}
                bgColor="bg-purple-50"
              />
            </div>

            {/* Quick Actions */}
            <Card className="border-sky-100 shadow-lg">
              <CardHeader>
                <CardTitle style={{fontFamily: 'Space Grotesk, sans-serif'}}>Quick Actions</CardTitle>
                <CardDescription>Manage your examination system</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-3 gap-4">
                  <ActionCard
                    title="Manage Students"
                    description="Add, edit, or remove students"
                    onClick={() => navigate('/admin/students')}
                    testId="action-students"
                  />
                  <ActionCard
                    title="Create Exam"
                    description="Set up new examinations"
                    onClick={() => navigate('/admin/exams')}
                    testId="action-exams"
                  />
                  <ActionCard
                    title="View Results"
                    description="Check student performance"
                    onClick={() => navigate('/admin/results')}
                    testId="action-results"
                  />
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </main>
    </div>
  );
}

function StatCard({ icon, title, value, bgColor }) {
  return (
    <Card className="border-sky-100 hover:shadow-lg transition-shadow">
      <CardContent className="pt-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-600 mb-1">{title}</p>
            <p className="text-3xl font-bold text-gray-900">{value}</p>
          </div>
          <div className={`p-3 rounded-full ${bgColor}`}>
            {icon}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function ActionCard({ title, description, onClick, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className="text-left p-6 border border-sky-100 rounded-lg hover:border-sky-300 hover:shadow-md transition-all"
    >
      <h3 className="text-lg font-semibold text-gray-900 mb-2">{title}</h3>
      <p className="text-sm text-gray-600">{description}</p>
    </button>
  );
}